// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Image, Pressable, StyleSheet, TextInput, Platform, ScrollView } from 'react-native';
import { Redirect, useRouter, useRootNavigationState } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { Text, View } from '@/components/Themed';
import { useUser } from './user-context';
import { useI18n } from '@/i18n';
import { AuthStatusCard } from '@/components/AuthStatusCard';
import { useRegisterUser } from '@/hooks/useRegisterUser';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getAssetImageSource } from '@/services/assetImageSource';
import appColors from '@/theme/colors';

const REGISTER_HERO_IMAGE_OPTIONS = {
  width: 1200,
  height: 474,
  bucketStep: 1,
} as const;

export default function RegisterScreen() {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);

  const { user } = useUser();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const isFocused = useIsFocused();
  const { t } = useI18n();
  const loginHeroImage = useMemo(
    () => getAssetImageSource('images/login-hero.png', '', '/register', REGISTER_HERO_IMAGE_OPTIONS),
    []
  );

  const [displayName, setDisplayName] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const { status, error, loading, socketState, sendCreateRequest } = useRegisterUser({
    apiBaseUrl,
    isFocused,
    displayName,
    fullName,
    email,
    password,
    onReset: () => {
      setDisplayName('');
      setFullName('');
      setEmail('');
      setPassword('');
    },
  });

  if (!isNavigationReady) return null;
  if (user) return <Redirect href="/welcome" />;

  return (
    <ScrollView contentContainerStyle={styles.container} style={styles.scroll}>
      <Image source={loginHeroImage} style={styles.hero} resizeMode="contain" />
      <AuthStatusCard
        style={styles.card}
        title={t('register.cardTitle')}
        status={status}
        error={error}
        buttonLabel={loading ? t('register.creating') : t('register.createCta')}
        onPress={sendCreateRequest}
        disabled={loading}
      >
        <Text style={styles.label}>{t('register.displayNameLabel')}</Text>
        <TextInput
          value={displayName}
          onChangeText={setDisplayName}
          placeholder={t('register.displayNamePlaceholder')}
          style={styles.input}
          autoCapitalize="words"
          placeholderTextColor="#8FA08B"
        />
        <Text style={styles.label}>{t('register.fullNameLabel')}</Text>
        <TextInput
          value={fullName}
          onChangeText={setFullName}
          placeholder={t('register.fullNamePlaceholder')}
          style={styles.input}
          autoCapitalize="words"
          placeholderTextColor="#8FA08B"
        />
        <Text style={styles.label}>{t('register.emailLabel')}</Text>
        <TextInput
          value={email}
          onChangeText={setEmail}
          placeholder={t('register.emailPlaceholder')}
          style={styles.input}
          keyboardType="email-address"
          autoCapitalize="none"
          placeholderTextColor="#8FA08B"
        />
        <Text style={styles.label}>{t('register.passwordLabel')}</Text>
        <TextInput
          value={password}
          onChangeText={setPassword}
          placeholder={t('register.passwordPlaceholder')}
          style={styles.input}
          secureTextEntry
          placeholderTextColor="#8FA08B"
        />
        <Text style={styles.connection}>
          {t('register.connectionLabel', { status: socketState === 'connected' ? t('register.connectionUp') : t('register.connectionDown') })}
        </Text>
        <Pressable style={styles.link} onPress={() => router.replace('/')}>
          <Text style={styles.linkText}>{t('register.backToLogin')}</Text>
        </Pressable>
      </AuthStatusCard>
    </ScrollView>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 4px 10px rgba(0,0,0,0.25)' },
  default: {
    shadowColor: '#C8C8B0',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 4,
  },
});

const styles = StyleSheet.create({
  container: {
    padding: 16,
    alignItems: 'center',
    gap: 12,
  },
  scroll: { flex: 1, backgroundColor: '#011806' },
  hero: {
    width: '100%',
    maxWidth: 720,
    aspectRatio: 1200 / 474,
    marginTop: 20,
  },
  card: {
    width: '100%',
    maxWidth: 900,
  },
  label: {
    color: appColors.text.primary,
    fontWeight: '700',
    marginBottom: 6,
    marginTop: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: '#1A3A20',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: appColors.text.primary,
    backgroundColor: '#0C2510',
  },
  connection: {
    color: '#8FA08B',
    fontSize: 12,
    marginTop: 12,
  },
  link: {
    marginTop: 12,
    alignItems: 'center',
  },
  linkText: {
    color: '#A1D493',
    fontWeight: '700',
  },
});




function generateRequestId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as { randomUUID?: () => string }).randomUUID?.() ?? `req-${Date.now()}`;
  }
  const random = Math.random().toString(36).slice(2, 8);
  return `req-${Date.now()}-${random}`;
}
